import React, { useState } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Button, Modal, Field, Select } from "../ui";
import { ADVISOR_ICON } from "./AdvisorCard";
import { CheckSquare, FolderPlus, Clock, Check, X, ListChecks } from "lucide-react";

const PRIORITY_STYLE = {
  high: "bg-red-500/12 text-red-500",
  medium: "bg-amber-500/12 text-amber-500",
  low: "bg-text-secondary/12 text-text-secondary",
};
const STATUS_STYLE = {
  open: "text-text-secondary",
  snoozed: "text-amber-500",
  accepted: "text-emerald-500",
  ignored: "text-text-secondary line-through",
  done: "text-emerald-500",
};

function TargetModal({ open, onClose, action, actionBase, meta, onCreated }) {
  const { t, toast } = useApp();
  const isProject = action?.type === "project";
  const entityFromKey = meta.entities.find((e) => e.key === action?.entity_key);
  const [entityId, setEntityId] = useState(entityFromKey?.id || meta.entities[0]?.id || "");
  const [spaceId, setSpaceId] = useState("");
  const [saving, setSaving] = useState(false);
  const spaces = meta.spaces.filter((s) => s.entity_id === entityId);

  React.useEffect(() => {
    const first = meta.spaces.find((s) => s.entity_id === entityId);
    setSpaceId(first?.id || "");
  }, [entityId, meta.spaces]);

  const submit = async () => {
    setSaving(true);
    try {
      const url = `${actionBase}/actions/${action.id}/${isProject ? "project" : "task"}`;
      const body = isProject ? { entity_id: entityId, space_id: spaceId } : { entity_id: entityId };
      await api.post(url, body);
      toast(t("adv_created_ok"));
      onCreated();
      onClose();
    } catch {
      toast(t("adv_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("adv_pick_target")}
      footer={<Button onClick={submit} disabled={saving || !entityId || (isProject && !spaceId)} data-testid="adv-target-confirm">
        {isProject ? t("adv_create_project") : t("adv_create_task")}</Button>}>
      <p className="text-sm text-text-secondary mb-3">{action?.title}</p>
      <Field label={t("adv_select_entity")}>
        <Select value={entityId} onChange={(e) => setEntityId(e.target.value)} data-testid="adv-target-entity">
          {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </Select>
      </Field>
      {isProject && (
        <Field label={t("adv_select_space")}>
          <Select value={spaceId} onChange={(e) => setSpaceId(e.target.value)} data-testid="adv-target-space">
            {spaces.length === 0 && <option value="">—</option>}
            {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
      )}
    </Modal>
  );
}

function ActionRow({ action, actionBase, meta, onChange }) {
  const { t } = useApp();
  const Icon = ADVISOR_ICON[action.advisor_key] || ListChecks;
  const [target, setTarget] = useState(false);
  const setStatus = async (status) => {
    await api.patch(`${actionBase}/actions/${action.id}`, { status });
    onChange();
  };
  const isClosed = ["accepted", "ignored", "done"].includes(action.status);
  return (
    <div data-testid={`adv-action-${action.id}`}
      className={cn("bg-surface rounded-lg border p-3 flex flex-col gap-2", action.status === "ignored" && "opacity-60")}>
      <div className="flex items-start gap-2.5">
        <Icon className="w-4 h-4 text-text-secondary mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium leading-snug", STATUS_STYLE[action.status])}>{action.title}</p>
          {action.rationale && <p className="text-xs text-text-secondary mt-0.5 leading-snug">{action.rationale}</p>}
        </div>
        <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0", PRIORITY_STYLE[action.priority])}>
          {action.priority}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap pl-6.5">
        {action.status !== "open" && (
          <span className={cn("text-[11px] font-medium mr-1", STATUS_STYLE[action.status])}>{t(`adv_act_${action.status}`)}</span>
        )}
        {!isClosed && (
          <>
            <Button size="sm" variant="ghost" onClick={() => setTarget(true)} data-testid={`adv-action-create-${action.id}`}>
              {action.type === "project" ? <><FolderPlus className="w-3.5 h-3.5 mr-1" />{t("adv_create_project")}</>
                : <><CheckSquare className="w-3.5 h-3.5 mr-1" />{t("adv_create_task")}</>}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setStatus("snoozed")} data-testid={`adv-action-snooze-${action.id}`}>
              <Clock className="w-3.5 h-3.5 mr-1" />{t("adv_snooze")}</Button>
            <Button size="sm" variant="ghost" onClick={() => setStatus("accepted")} data-testid={`adv-action-accept-${action.id}`}>
              <Check className="w-3.5 h-3.5 mr-1" />{t("adv_accept")}</Button>
            <Button size="sm" variant="ghost" onClick={() => setStatus("ignored")} data-testid={`adv-action-ignore-${action.id}`}>
              <X className="w-3.5 h-3.5 mr-1" />{t("adv_ignore")}</Button>
          </>
        )}
      </div>
      <TargetModal open={target} onClose={() => setTarget(false)} action={action} actionBase={actionBase} meta={meta} onCreated={onChange} />
    </div>
  );
}

export function ActionList({ actions, actionBase, meta, onChange }) {
  const { t } = useApp();
  if (!actions?.length) return null;
  return (
    <div data-testid="adv-action-list">
      <h2 className="font-heading font-bold text-base mb-3 flex items-center gap-2">
        <ListChecks className="w-4 h-4" />{t("adv_actions")}
      </h2>
      <div className="space-y-2">
        {actions.map((a) => <ActionRow key={a.id} action={a} actionBase={actionBase} meta={meta} onChange={onChange} />)}
      </div>
    </div>
  );
}
